import React from 'react';
import { Spinner, Tooltip } from 'design-system';
import { useObjectPreview } from 'corp/hooks/smartId/useObjectPreview';
import { ICoursePreviewJson, INamedObjectTypes } from '@scale/shared/types/lookup';
import IsomorphicLink from 'corp/components/UI/IsomorphicLink';
import RemotasksExternalLink from 'corp/components/UI/RemotasksExternalLink';
import ProductPill from 'corp/components/UI/ProductPill';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import _ from 'lodash';
import CourseTypePill from 'corp/components/UI/CourseTypePill';
import SimpleQuestionMark from 'corp/components/UI/SimpleQuestionMark';
import { useRemoAdminCtx } from 'react-scale/components/remoAdmin/remoAdminContext';

const ProjectPreview: React.FC<{ id: string }> = ({ id }) => {
  const remoAdminCtx = useRemoAdminCtx();
  const {
    value: course,
    loading,
    error,
  } = useObjectPreview<ICoursePreviewJson>(id, INamedObjectTypes.Course);
  if (error) return <span>Sorry, there was an error</span>;
  if (loading) return <Spinner />;
  const sectionCounts =
    course &&
    (Object.entries(_.countBy(course.sections, 'type')).sort(
      (a, b) => ((b[1] as number) - a[1]) as number,
    ) as [string, number][]);

  return (
    <div className="course-preview">
      <style jsx>{`
        .course-preview__banner {
          font-weight: bold;
          padding: 10px;
          display: flex;
          align-items: center;
          flex-wrap: wrap;
          justify-content: flex-start;
        }

        .course-preview__summary {
          display: flex;
          flex-wrap: wrap;
          padding: 0 10px;
          justify-content: center;
        }

        .course-preview__summary > span {
          display: flex;
          flex-grow: 1;
          justify-content: center;
          padding: 3px 6px;
        }

        .course-preview__actions {
          display: flex;
          flex-direction: column;
        }

        .course-preview__actions > * {
          padding: 5px;
          display: flex;
          margin-left: 10px;
        }
      `}</style>
      {course && (
        <>
          <div className="course-preview__banner">
            <h3 style={{ margin: '0 5px' }}>{course.title}</h3>
          </div>
          <div className="course-preview__body">
            <div className="course-preview__summary">
              {course.disabled ? (
                <span style={{ fontWeight: 'bold', color: 'var(--color-RemoRed)' }}>Disabled</span>
              ) : (
                <span style={{ fontWeight: 'bold', color: 'var(--color-RemoGreen)' }}>
                  {' '}
                  <span style={{ marginRight: '6px' }}>
                    {' '}
                    <FontAwesomeIcon icon={'check-circle'} />
                  </span>
                  Enabled
                </span>
              )}
              {!course.available ? (
                <span style={{ fontWeight: 'bold', color: 'var(--color-RemoRed)' }}>Private</span>
              ) : (
                <span style={{ fontWeight: 'bold', color: 'var(--color-RemoGreen)' }}>
                  {' '}
                  <span style={{ marginRight: '6px' }}>
                    {' '}
                    <FontAwesomeIcon icon={'check-circle'} />
                  </span>
                  Public
                </span>
              )}
              <span>
                <Tooltip
                  title={
                    !!sectionCounts && (
                      <div>
                        {sectionCounts.map(([type, count]) => (
                          <div key={type}>
                            {type}: {count}
                          </div>
                        ))}
                      </div>
                    )
                  }
                >
                  <div>
                    <span>{course.sections.length} sections</span>
                    <SimpleQuestionMark />{' '}
                  </div>
                </Tooltip>
              </span>
            </div>
            <div className="course-preview__actions">
              <span>
                <IsomorphicLink
                  target="_blank"
                  rel="noopener noreferrer"
                  href={`${remoAdminCtx.isRemoAdmin ? '/remoadmin/tools' : ''}/courses/${
                    course._id
                  }`}
                >
                  {' '}
                  <FontAwesomeIcon icon={'edit'} />
                  <span style={{ marginLeft: '6px' }}>Edit {course.name}</span>
                </IsomorphicLink>
              </span>
              <span>
                <IsomorphicLink
                  target="_blank"
                  rel="noopener noreferrer"
                  href={`${remoAdminCtx.isRemoAdmin ? '/remoadmin/tools/courses' : '/lookup'}/${
                    course._id
                  }`}
                >
                  {' '}
                  <FontAwesomeIcon icon={'search'} />
                  <span style={{ marginLeft: '6px' }}>Lookup {course._id}</span>
                </IsomorphicLink>
              </span>
              <span>
                <RemotasksExternalLink path={'/course?id=' + course._id}>
                  <FontAwesomeIcon icon={'external-link'} />
                  <span style={{ marginLeft: '6px' }}>Open in Remotasks</span>
                </RemotasksExternalLink>
              </span>
              <span>
                <ProductPill type={course.taskType} />
                {course?.courseType && <CourseTypePill type={course.courseType} compact={true} />}
              </span>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ProjectPreview;
